import { Collections } from '../collections.model';
import {
  assertNever,
  isDefined,
  isEqual,
  jsonStableStringify,
  partition,
  sameElements,
  setDiff,
} from './helpers';
import {
  Entities,
  Entity,
  EntityState,
  Lists,
  Path,
  QueryParam,
  QueryParams,
  RawQuery,
  Reference,
  StoreFeatureState,
  StoreState,
} from './store.model';

// If an entity matches a query. If there's no query then it does so trivially.
function entityMatchesQueryParams<T>(
  entity: Entity<T>,
  params?: QueryParams<T>,
) {
  return params
    ? // TODO: QueryParams entries are too complicated for TypeScript to
      // understand that it suddenly just assumes {}
      Object.values<any>(params).every((param: QueryParam<T>) => {
        if (param.name in entity.entity) {
          const entityValue = entity.entity[param.name];

          switch (param.condition) {
            case '==':
              return isEqual(entityValue, param.value);
            case 'in':
              return param.values.some(value => isEqual(entityValue, value));
            default:
              return assertNever(param);
          }
        }

        return false;
      })
    : true;
}

export function stringifyQuery<K extends keyof Collections>(
  query: RawQuery<Collections[K]> | undefined,
) {
  if (query) {
    // Format to [key, condition, value], ignoring undefined values
    type KeyConditionValue = [string, QueryParam<K>['condition'], any];

    const params: KeyConditionValue[] = query.params
      ? // TODO: QueryParams entries are too complicated for TypeScript to
        // understand that it suddenly just assumes {}
        Object.entries(<QueryParams<{
          [k: string]: QueryParam<K>;
        }>>query.params).map<KeyConditionValue>(([key, entry]) => [
          key,
          entry.condition,
          (entry.condition === '==' && entry.value) ||
            (entry.condition === 'in' && entry.values),
        ])
      : [];

    const pagination = { ...query, params: undefined };

    const entries = Object.entries(pagination)
      .map<KeyConditionValue>(([key, value]) => [key, '==', value])
      .concat(params)
      .filter(([key, condition, value]) => !!value)
      .sort()
      .map(
        ([key, condition, value]) =>
          `${key}${condition}${jsonStableStringify(value, true)}`,
      )
      .join(';');

    return entries || 'default';
  }

  return 'default';
}

/* This merges a number of entities into the state.
 * If an offset it specified then the new entities are placed at that offset.
 * If an offset is not specified then the new entities will be:
 *   - merged if they have the same id as an existing entity OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 */
export function mergeEntities<K extends keyof Collections>(
  state: StoreState[K],
  entities: Entities<Collections[K]>,
  ref: Reference<K>,
): StoreState[K] {
  const data =
    state[ref.path.authUid] && state[ref.path.authUid].entities
      ? state[ref.path.authUid]
      : { entities: {}, lists: {} };
  const queryString = stringifyQuery<K>(ref.query);

  const updatedEntities = {
    ...data.entities,
    ...entities,
  };

  const newListIds = Object.keys(entities).filter(key => {
    const matches = entityMatchesQueryParams(
      entities[key],
      ref.query ? ref.query.params : undefined,
    );
    if (!matches) {
      console.error(
        'Attempted to merge invalid entity into list: ',
        ref.path.collection,
        ref.query,
        entities[key],
      );
    }
    return matches;
  });

  return Object.assign({}, state, {
    [ref.path.authUid]: {
      entities: updatedEntities,
      lists: {
        ...data.lists,
        ...{
          [queryString]: {
            ids: newListIds,
            query: ref.query,
            // TODO: sortField information in string, not function
          },
        },
      },
    },
  });
}

/* This merges a number of entities into the state.
 * If an offset it specified then the new entities are placed at that offset.
 * If an offset is not specified then the new entities will be:
 *   - merged if they have the same id as an existing entity OR
 *   - placed at the start of the list (and the offset of existing items moved up)
 *
 * We also allow a custom sorting function to allow resorting the list.
 * If this is used all the offsets will be then be reindexed from 0.
 * TODO T40096: This is a websocket version of mergeEntities, which has more
 * logic right now to apply the websocket data on the right place of the store
 * slice. Maybe we can merge this with mergeEntities again?
 */
export function mergeWebsocketEntities<K extends keyof Collections>(
  state: StoreState[K],
  entities: Entities<Collections[K]>,
  ref: Reference<K>,
  sortField?: (entity: Collections[K]) => number | string,
): StoreState[K] {
  const data: EntityState<Collections[K]> =
    state[ref.path.authUid] && state[ref.path.authUid].entities
      ? state[ref.path.authUid]
      : { entities: {}, lists: {} };

  const updatedEntities = {
    ...data.entities,
    ...entities,
  };

  if (!data.lists) {
    return state;
  }

  const updatedLists = Object.entries(data.lists).reduce(
    (list, [key, entry]) => {
      // TODO: Type annotation is unnecessary if List.query is typed properly.
      const { query }: { query?: RawQuery<Collections[K]> } = entry || {
        query: {},
      };

      const { params, offset, limit } = query || {
        params: undefined,
        offset: undefined,
        limit: undefined,
      };

      // If the offset is non-zero we don't add it to the new list.
      if (offset) {
        return list;
      }

      // Existing entity ids of the old list
      const currEntityIds = entry ? Array.from(entry.ids) : [];

      // Find which entities modified by the websocket now match (or don't match)
      // the query. These should be added (or removed) from the list
      const [matchingIds, nonMatchingIds] = partition(
        Object.entries(entities),
        ([entityId, entity]) => entityMatchesQueryParams(entity, params),
      ).map(entityEntries => entityEntries.map(([entityId]) => entityId));

      // The new list is the old list plus any matching entity ids, minus any
      // non-matching entity ids, while ensuring all ids are unique
      const filteredEntityIds = setDiff(
        currEntityIds.concat(matchingIds),
        nonMatchingIds,
      );

      // Finally, apply limit to the ids before merging into list
      const updatedEntityIds = filteredEntityIds.slice(0, limit);

      return {
        ...list,
        [key]: {
          ...entry,
          ids: updatedEntityIds,
        },
      };
    },
    {} as Lists<Collections[K]>,
  );

  return Object.assign({}, state, {
    [ref.path.authUid]: {
      entities: updatedEntities,
      lists: {
        ...data.lists,
        ...updatedLists,
      },
    },
  });
}

/**
 * Add/overwrite a list of entities to the datastore that are from a websocket.
 * If you instead want to modify an existing object use
 * `transformWebsocketEntities`.]
 * If your transformer maps to `undefined` then the entity will be ignored.
 */
export function addWebsocketEntities<K extends keyof Collections, O>(
  state: StoreState[K],
  list: O[],
  transform: (a: O) => Collections[K] | undefined,
  ref: Reference<K>,
  sortField?: (entity: Collections[K]) => number | string,
): StoreState[K];
export function addWebsocketEntities<K extends keyof Collections, O, E>(
  state: StoreState[K],
  list: O[],
  transform: (a: O, extra: E) => Collections[K] | undefined,
  ref: Reference<K>,
  extra: E,
  sortField?: (entity: Collections[K]) => number | string,
): StoreState[K];
export function addWebsocketEntities<K extends keyof Collections, O, E>(
  state: StoreState[K],
  list: O[],
  transform: (a: O, extra?: E) => Collections[K] | undefined,
  ref: Reference<K>,
  extra?: E,
  sortField?: (entity: Collections[K]) => number | string,
): StoreState[K] {
  return mergeWebsocketEntities<K>(
    state,
    transformOptionalWebsocketObjects(list, transform, extra),
    ref,
    sortField,
  );
}

/**
 * Take what is in the datastore and run a transformation on it.
 * If an item is not found in the store then it is ignored.
 */
export function updateWebsocketEntities<K extends keyof Collections>(
  state: StoreState[K],
  list: string[] | number[],
  transform: (a: Collections[K]) => Collections[K],
  ref: Reference<K>,
  sortField?: (entity: Collections[K]) => number | string,
): StoreState[K] {
  return mergeWebsocketEntities<K>(
    state,
    transformOptionalWebsocketObjects<Collections[K], string | number>(
      list,
      id => {
        const object = pluckItemFromRawStoreSlice(state, ref.path, id);
        return object ? transform(object) : undefined;
      },
    ),
    ref,
    sortField,
  );
}

interface ObjectWithId {
  id: number | string;
}

export function transformObjects<T extends ObjectWithId, O>(
  list: O[] | { [id: number]: O } | undefined,
  transform: (a: O) => T,
): Entities<T>;

export function transformObjects<T extends ObjectWithId, O, E>(
  list: O[] | { [id: number]: O } | undefined,
  transform: (a: O, extra: E) => T,
  extra: E,
): Entities<T>;

export function transformObjects<T extends ObjectWithId, O, E>(
  list: O[] | { [id: number]: O } | undefined,
  transform: (a: O, extra?: E) => T,
  extra?: E,
): Entities<T> {
  if (!list) {
    return {};
  }

  const actualList = Array.isArray(list) ? list : Object.values<O>(list);

  return actualList
    .map(o => transform(o, extra))
    .filter(isDefined)
    .map(entity => ({
      [entity.id]: {
        entity,
        timeFetched: Date.now(),
        timeUpdated: Date.now(),
      },
    }))
    .reduce((acc: Entities<T>, item) => ({ ...acc, ...item }), {});
}

/** This should only be used for websocket transformers as we shouldn't skip
 * objects when transforming get requests or it will break pagination.
 * It should not be exported.
 */
function transformOptionalWebsocketObjects<T extends ObjectWithId, O>(
  list: O[],
  transform: (a: O) => T | undefined,
): Entities<T>;
function transformOptionalWebsocketObjects<T extends ObjectWithId, O, E>(
  list: O[],
  transform: (a: O, extra: E) => T | undefined,
  extra: E,
): Entities<T>;
function transformOptionalWebsocketObjects<T extends ObjectWithId, O, E>(
  list: O[],
  transform: (a: O, extra?: E) => T | undefined,
  extra?: E,
): Entities<T> {
  return list
    .map(o => transform(o))
    .filter(isDefined)
    .map(entity => ({
      [entity.id]: {
        entity,
        timeFetched: Date.now(),
        timeUpdated: Date.now(),
      },
    }))
    .reduce((acc: Entities<T>, item) => ({ ...acc, ...item }), {});
}

export function pathsEqual<K extends keyof Collections>(
  a: Path<K>,
  b: Path<K>,
) {
  return (
    a.collection === b.collection &&
    a.authUid === b.authUid &&
    sameElements(a.ids || [], b.ids || [])
  );
}

/**
 * Check if both QueryParams are equal down to the QueryParam values.
 * @param a QueryParams to compare.
 * @param b Another QueryParams to compare.
 * @return boolean Whether or not it's the same.
 */
function queryParamsEqual<T>(a: QueryParams<T>, b: QueryParams<T>) {
  const aQuery = stringifyQuery(a);
  const bQuery = stringifyQuery(b);

  return aQuery === bQuery;
}

export function referencesEqual<K extends keyof Collections>(
  a: Reference<K>,
  b: Reference<K>,
) {
  return (
    pathsEqual(a.path, b.path) &&
    ((a.query === undefined && b.query === undefined) ||
      (a.query !== undefined &&
        b.query !== undefined &&
        a.query.offset === b.query.offset &&
        a.query.limit === b.query.limit &&
        (a.query.params !== undefined &&
          b.query.params !== undefined &&
          queryParamsEqual(a.query.params, b.query.params))))
  );
}

export function pluckItemFromRawStore<K extends keyof Collections>(
  store: StoreState,
  path: Path<K>,
  id: string | number,
): Collections[K] | undefined {
  return store[path.collection][path.authUid].entities[id]
    ? store[path.collection][path.authUid].entities[id].entity
    : undefined;
}

export function pluckItemFromRawStoreSlice<K extends keyof Collections>(
  storeSlice: StoreFeatureState<K>,
  path: Path<K>,
  id: string | number,
): Collections[K] | undefined {
  if (!storeSlice[path.authUid] || !storeSlice[path.authUid].entities[id]) {
    return undefined;
  }
  return storeSlice[path.authUid].entities[id].entity;
}

export function removeEntityById<K extends keyof Collections>(
  ref: Reference<K>,
  state: StoreState[K],
  id: string | number,
): StoreState[K] {
  const data = state[ref.path.authUid] || { lists: {}, entities: {} };
  const entities = { ...data.entities };
  delete entities[id];

  // Remove the deleted ID from any list
  const lists = Object.entries(<Lists<Collections[K]>>data.lists).reduce(
    (obj, [queryString, list]) => {
      const newIds = (list && list.ids ? list.ids : []).filter(
        listId => listId.toString() !== id.toString(),
      );

      return {
        ...obj,
        [queryString]: {
          ...list,
          ids: newIds,
        },
      };
    },
    <Lists<Collections[K]>>{},
  );

  return Object.assign({}, state, {
    [ref.path.authUid]: {
      entities,
      lists,
    },
  });
}

/**
 * Gets the query param value of a specific query param name and returns it in
 * an array.
 * @param query The query.
 * @param name The key of the entity for the query.
 * @returns An array of the values.
 */
export function getQueryParamValue<T, K extends keyof T>(
  query: RawQuery<T>,
  name: K,
): T[K][] {
  if (query.params && query.params[name]) {
    const param: QueryParam<T, K> = query.params[name];
    switch (param.condition) {
      case '==': {
        return [param.value];
      }

      case 'in': {
        return param.values;
      }

      default: {
        return [];
      }
    }
  }

  return [];
}



// WEBPACK FOOTER //
// ./src/app/@shared/datastore/core/store.helpers.ts